/**
 * The last scrub before an in-app AI tool result becomes prompt text
 * (04.10.2026, card "In-app AI: audit task_board_read (and friends) for the
 * same leaks as MCP").
 *
 * The in-app assistant talks to a THIRD-PARTY model provider (the user's own
 * key, or the Apiboo assistant) — the same exposure as an MCP client. MCP got
 * one scrub at the single place every JS tool result passes (`runHandler`,
 * src/mcp/handlers/index.js). This is the in-app counterpart:
 * `condenseToolResultForApi` (AIChatPanel.js) runs every tool result through
 * `scrubForModel` before any case reads it, so a tool added tomorrow cannot
 * forget.
 *
 * Two passes, both REUSED, never copied:
 *  1. image decryption keys — `scrubMediaKeysDeep` (src/mcp/mediaKeys.js):
 *     `data-enc-key` / `data-enc` in any string and `k` on an encrypted
 *     media node. The blobs are public-read, so URL + key decrypts the file.
 *  2. credential shapes in free text — `redactSecretsInText`
 *     (secretHeuristics.js): `scheme://user:pass@`, `?api_key=…`,
 *     `Bearer …`, vendor key formats, PEM blocks. Request URLs, history URLs
 *     and error messages are free text the user typed; this is what keeps a
 *     pasted credential in one of them out of the transcript.
 *
 * What this does NOT do: decide what a tool may say. That is still each
 * tool's explicit `case` (and the default projector for an unlisted one). A
 * scrub is a backstop for shapes, not a minimisation policy.
 */
import { scrubMediaKeysDeep, stripMediaKeyAttrs } from '../../mcp/mediaKeys';
import { redactSecretsInText } from './secretHeuristics';

/** Copy-on-write walk: every string through `redactSecretsInText`. */
function redactStringsDeep(value) {
  if (typeof value === 'string') return redactSecretsInText(value);
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((item) => {
      const next = redactStringsDeep(item);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : value;
  }
  let changed = false;
  const out = {};
  for (const [key, item] of Object.entries(value)) {
    const next = redactStringsDeep(item);
    if (next !== item) changed = true;
    out[key] = next;
  }
  return changed ? out : value;
}

/**
 * A tool result as the model may see it: no image key, no credential-shaped
 * text. Copy-on-write — results can be live store objects, and the UI keeps
 * the original (the panel renders it locally; it never leaves the machine).
 */
export function scrubForModel(result) {
  return redactStringsDeep(scrubMediaKeysDeep(result));
}

/** The same two passes for one string (history lines, mention context). */
export function scrubTextForModel(text) {
  if (typeof text !== 'string' || !text) return text;
  return redactSecretsInText(stripMediaKeyAttrs(text));
}
